import Pill from '@pelckmans/elementis/components/pill';
import TasksIcon from '@pelckmans/elementis/icons/tasks';
import UserIcon from '@pelckmans/elementis/icons/user';
import { ComponentProps, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { NodeWithoutDescription } from '../../../../../api/table-of-content';
import { isSharedUserMaterial, LtiSharedUserMaterial, LtiUserMaterial } from '../../../../../api/user-material';
import IdentityContext from '../../../../../contexts/IdentityContext';
import usePlatformName from '../../../../../hooks/usePlatformName';
import { LtiExerciseModal } from '../../../../../shared-components/elementis/components/lti-exercise-modal/LTIExerciseModal';
import { formatAssignmentToDate } from '../../../../../utils/formatDate';
import { TOKEN_ACCESSOR } from '../../../../../utils/tokenAccessor';
import { buildLtiExerciseMaterial } from '../../../../../utils/user-material/buildLtiExerciseMaterial';
import ModuleContext from '../../../_contexts/ModuleContext';
import { UserMaterialTags } from '../../../components/UserMaterialDialogHeaderTags';
import { assignmentsEndIn24hours, assignmentsEndIn7Days } from '../../../utils/assignmentUtils';

const MODAL_ID = 'lti-user-material-modal';

const { REACT_APP_P_STUDIO_LANGUAGE, REACT_APP_LTI_AUTH_URL, REACT_APP_P_STUDIO_GROUP } = process.env;

const getTranslationsForLtiExerciseModal = (t: ReturnType<typeof useTranslation>['t']) => ({
  confirmationModals: {
    title: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.TITLE'),
    ariaLabel: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.A11Y_LABEL'),
    actions: {
      close: t('PAGES.TOC_LEVEL.MODALS.LTI.ACTIONS.CLOSE'),
      cancel: t('PAGES.TOC_LEVEL.MODALS.LTI.ACTIONS.CANCEL'),
      restart: t('PAGES.TOC_LEVEL.MODALS.LTI.ACTIONS.RESTART'),
    },
    body: {
      currentResult: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.CURRENT_RESULT'),
      score: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.SCORE'),
      percent: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.PERCENT'),
      notRepeatable: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.NOT_REPEATABLE'),
      repeatable: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.REPEATABLE'),
      exerciseTitle: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.EXERCISE'),
    },
  },
  exerciseModal: {
    ariaLabel: t('PAGES.TOC_LEVEL.MODALS.LTI.A11Y_LABEL'),
  },
});

export default function useLtiUserMaterial() {
  const { t } = useTranslation();
  const platformName = usePlatformName();

  const { module, subModules } = useContext(ModuleContext);
  const { identity } = useContext(IdentityContext);

  async function openLtiUserMaterial(
    material: LtiUserMaterial | LtiSharedUserMaterial,
    nodes: NodeWithoutDescription[],
    onModalClosed?: () => Promise<void>,
  ) {
    if (!module) throw new Error('Module context not found');

    const materialModule = [module, ...subModules].find(m => m.id === material.moduleId);

    if (!materialModule) throw new Error('Module for material not found');

    const pillProminence = (): ComponentProps<typeof Pill>['prominence'] => {
      if (!material.assignment) return undefined;

      if (assignmentsEndIn24hours(material.assignment)) {
        return 'danger';
      } else if (assignmentsEndIn7Days(material.assignment)) {
        return 'warning';
      }

      return 'neutral';
    };

    return (
      <LtiExerciseModal
        id={MODAL_ID}
        ltipsUrl={REACT_APP_LTI_AUTH_URL}
        studioLanguage={REACT_APP_P_STUDIO_LANGUAGE}
        studioGroup={REACT_APP_P_STUDIO_GROUP}
        material={buildLtiExerciseMaterial(material)}
        module={materialModule}
        platformName={platformName}
        tokenAccessor={TOKEN_ACCESSOR}
        isOwner={identity.isTeacherAlike}
        onClose={() => onModalClosed?.()}
        translations={getTranslationsForLtiExerciseModal(t)}
        tags={
          <>
            <UserMaterialTags
              material={material}
              isTeacherAlike={identity.isTeacherAlike}
              nodes={nodes}
              module={materialModule}
            />
            {isSharedUserMaterial(material) && material.assignment && (
              <>
                <Pill key="assignment-pill" icon={<TasksIcon />}>
                  {t('EXERCISE_MANAGER.ACTIVE_SESSION.PILLS.ASSIGNMENT')}
                </Pill>
                <Pill key="deadline-pill" prominence={pillProminence()}>
                  {t('EXERCISE_MANAGER.ACTIVE_SESSION.PILLS.TO', {
                    to: formatAssignmentToDate(material.assignment.to),
                  })}
                </Pill>
                <Pill
                  key="user-pill"
                  icon={<UserIcon />}
                >{`${material.owner.firstName} ${material.owner.lastName}`}</Pill>
              </>
            )}
          </>
        }
      />
    );
  }

  return { openLtiUserMaterial };
}
